import { Fragment } from 'react';
import { GlobeFillIcon } from '@zep-us/zus';
import { Blue500, ICON_COLOR, Red500, White } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { PlusSmallIcon, YoutubeFillIcon } from '@/icons';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { Button, type ButtonProps, Tooltip } from '@/ui_v3';
import { cn } from '@/utils';
import { RollingFileIcons } from './RollingFileIcons';
export const MaterialAddButtons = ({
  onFileClick,
  onYoutubeClick,
  onWebsiteClick,
  disabled
}: {
  onFileClick: () => void;
  onYoutubeClick: () => void;
  onWebsiteClick: () => void;
  disabled?: boolean;
}) => {
  return <Fragment>
      <FileMaterialAddButton disabled={disabled} onClick={onFileClick} data-sentry-element="FileMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx" />
      <YoutubeMaterialAddButton disabled={disabled} onClick={onYoutubeClick} data-sentry-element="YoutubeMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx" />
      <WebsiteMaterialAddButton disabled={disabled} onClick={onWebsiteClick} data-sentry-element="WebsiteMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx" />
    </Fragment>;
};
const FileMaterialAddButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="FileMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx">
      <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
        <Tooltip.Trigger data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
          <MaterialAddButtonBase disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={e => {
          e.stopPropagation();
          onClick();
        }} leftSlot={<RollingFileIcons />} data-sentry-element="MaterialAddButtonBase" data-sentry-source-file="MaterialAddButton.tsx">
            {t('build:build.ai.material.file')}
          </MaterialAddButtonBase>
        </Tooltip.Trigger>
        <Tooltip.Content side="top" align="start" alignOffset={-10} className="max-w-[300px] bg-default text-body-sm-semibold text-default" data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
          <Tooltip.Arrow style={{
          fill: White
        }} data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx" />
          {t('build:build.ai.material.fileTooltip')}
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>;
};
const YoutubeMaterialAddButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="YoutubeMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx">
      <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
        <Tooltip.Trigger data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
          <MaterialAddButtonBase disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={e => {
          e.stopPropagation();
          onClick();
        }} leftSlot={<YoutubeFillIcon size="xs" fill={Red500} />} data-sentry-element="MaterialAddButtonBase" data-sentry-source-file="MaterialAddButton.tsx">
            {t('build:build.ai.material.youtube')}
          </MaterialAddButtonBase>
        </Tooltip.Trigger>
        <Tooltip.Content side="top" className="max-w-[300px] bg-default text-body-sm-semibold text-default" data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
          <Tooltip.Arrow style={{
          fill: White
        }} data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx" />
          {t('build:build.ai.material.youtubeTooltip')}
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>;
};
const WebsiteMaterialAddButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <div className="relative" data-sentry-component="WebsiteMaterialAddButton" data-sentry-source-file="MaterialAddButton.tsx">
      <BetaChip size="xs" variant="outlined" className="absolute -right-0 -top-0 h-[22px] -translate-x-[4px] -translate-y-1/2 border-[2px] border-inverse bg-yellow-700 text-inverse" data-sentry-element="BetaChip" data-sentry-source-file="MaterialAddButton.tsx" />

      <Tooltip.Provider data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
        <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
          <Tooltip.Trigger data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
            <MaterialAddButtonBase disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={e => {
            e.stopPropagation();
            onClick();
          }} leftSlot={<GlobeFillIcon size="xs" fill={Blue500} />} data-sentry-element="MaterialAddButtonBase" data-sentry-source-file="MaterialAddButton.tsx">
              {t('build:build.website.materialAdd.label')}
            </MaterialAddButtonBase>
          </Tooltip.Trigger>
          <Tooltip.Content side="top" className="max-w-[300px] bg-default text-body-sm-semibold text-default" data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx">
            <Tooltip.Arrow style={{
            fill: White
          }} data-sentry-element="unknown" data-sentry-source-file="MaterialAddButton.tsx" />
            {t('build:build.website.materialAdd.description')}
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    </div>;
};
const MaterialAddButtonBase = (props: Omit<ButtonProps, 'asChild'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <Button variant="contained" intent="neutral" size="md" rounded="full" className={cn('bg-gray-50 text-default text-body-md-semibold', className)} rightSlot={<PlusSmallIcon size="sm" fill={ICON_COLOR.neutral} />} {...rest} data-sentry-element="Button" data-sentry-component="MaterialAddButtonBase" data-sentry-source-file="MaterialAddButton.tsx">
      {children}
    </Button>;
};